import { yupResolver } from '@hookform/resolvers/yup';
import { Form, Typography } from 'antd';
import { FC, memo, useEffect } from 'react';
import { FormProvider, SubmitErrorHandler, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import { useProgramStepContext } from 'src/features/programsV2/contexts/ProgramStepContext';
import { useCreateOrUpdateProgram } from 'src/features/programsV2/hooks/useCreateOrUpdateProgram';
import {
  InitialStepForm,
  initialStepSchema,
} from 'src/features/programsV2/schemas/initialStepSchema';
import {
  showErrorModal,
  showSuccessModal,
} from 'src/features/programsV2/utils/modalHelpers';
import { getInitialProgramPayload } from 'src/features/programsV2/utils/steps/initialStep/getInitialProgramPayload';

import { ProgramStepFooter } from '../../ProgramStepFooter';
import { ProgramDetails } from './ProgramDetails';

const { Text, Paragraph } = Typography;

interface InitialStepProps {
  programId?: number;
  changeStep: (value: number) => void;
}

export const InitialStep: FC<InitialStepProps> = memo(
  ({ programId, changeStep }) => {
    const navigate = useNavigate();

    const {
      getStepDefaultValues,
      isLoadingProgram,
      isPublishedProgram,
      programApiInitialValues,
      stepValuesRef,
      shouldShowStepErrors,
    } = useProgramStepContext();

    const { createOrUpdateProgram, isLoading } =
      useCreateOrUpdateProgram(programId);

    const defaultValues = isPublishedProgram
      ? getStepDefaultValues('initialStep')
      : undefined;

    const values = !defaultValues
      ? programApiInitialValues?.initialStep
      : undefined;

    const form = useForm<InitialStepForm>({
      resolver: yupResolver(initialStepSchema),
      defaultValues: defaultValues || undefined,
      values,
      mode: 'onChange',
    });

    const updatedValues = form.watch();

    const isInitialStepValid = form.formState.isValid;

    const handleUpdateProgram = async (fields: InitialStepForm) => {
      try {
        const payload = getInitialProgramPayload(fields);
        await createOrUpdateProgram(payload);
        showSuccessModal({
          title: 'Sucesso',
          content: 'Dados do programa atualizados com sucesso!',
        });
      } catch (error) {
        showErrorModal({
          title: 'Tivemos um problema...',
          content:
            'Não foi possível atualizar os dados do programa, tente novamente.',
        });
      }
    };

    const handleCreateProgram = async (fields: InitialStepForm) => {
      try {
        const payload = getInitialProgramPayload(fields);
        const id = await createOrUpdateProgram(payload);
        navigate(`/programs/v2/create-or-update/${id}`, { replace: true });
        showSuccessModal({
          title: 'Sucesso',
          content: 'Programa criado com sucesso!',
          afterClose: () => {
            changeStep(1);
          },
        });
      } catch (error) {
        showErrorModal({
          title: 'Tivemos um problema...',
          content: (
            <>
              <Paragraph>Faça algumas verificações nos dados:</Paragraph>
              <Text>
                1 - Verifique se já existe um programa com o mesmo nome.
              </Text>
            </>
          ),
        });
      }
    };

    const onSubmit = (fields: InitialStepForm) => {
      programId ? handleUpdateProgram(fields) : handleCreateProgram(fields);
    };

    const onError: SubmitErrorHandler<InitialStepForm> = errors => {
      const errorField = Object.keys(errors)[0];
      document
        .getElementsByName(errorField)[0]
        .scrollIntoView?.({ block: 'center' });
    };

    useEffect(() => {
      if (stepValuesRef.current && !isLoadingProgram && isPublishedProgram) {
        stepValuesRef.current['initialStep'] = {
          isValid: isInitialStepValid,
          formattedValues: getInitialProgramPayload(updatedValues),
          values: updatedValues,
        };
      }
    }, [updatedValues, isInitialStepValid, stepValuesRef]);

    useEffect(() => {
      if (shouldShowStepErrors && !isLoadingProgram) form.trigger();
    }, [form, shouldShowStepErrors, isLoadingProgram]);

    return (
      <FormProvider {...form}>
        <Form layout='vertical' onFinish={form.handleSubmit(onSubmit, onError)}>
          <ProgramDetails />
          <ProgramStepFooter
            programId={programId}
            removeNextButton={isPublishedProgram}
            isDisabledCancelButton={isLoading}
            isNextButtonDisabled={!form.formState.isValid || isLoading}
            isLoadingNextButton={isLoading}
            nextButtonText={programId ? 'Salvar' : 'Criar'}
            onCancelConfirmation={() => navigate('/programs/v2')}
            style={{
              left: 0,
              right: 0,
              bottom: 0,
              position: 'fixed',
            }}
          />
        </Form>
      </FormProvider>
    );
  }
);
